import { Injectable } from '@angular/core';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { capTask } from '@capacitor-community/sqlite';
import { BehaviorSubject } from 'rxjs';
import { OfflineDatabaseService } from './offline-database.service';
import {
  OfflineSyncConflictError,
  OfflineSyncModuleHandler,
  OfflineSyncOperation,
  OfflineSyncOperationInput,
  OfflineSyncQueueSummary,
  OfflineSyncStatus,
} from './offline-database.models';
import { SyncQueueRepository } from './sync-queue.repository';

const EMPTY_SUMMARY: OfflineSyncQueueSummary = {
  pending: 0,
  processing: 0,
  errors: 0,
  conflicts: 0,
};

@Injectable({ providedIn: 'root' })
export class OfflineSyncService {
  private readonly handlers = new Map<string, OfflineSyncModuleHandler>();
  private readonly statusSubject = new BehaviorSubject<OfflineSyncStatus>({
    state: Capacitor.isNativePlatform() ? 'inativo' : 'indisponivel',
    userId: 0,
    connected: true,
    lastSyncAt: null,
    summary: { ...EMPTY_SUMMARY },
  });
  private networkListener: PluginListenerHandle | null = null;
  private activeUserId = 0;
  private lifecycleToken = 0;
  private syncingPromise: Promise<void> | null = null;

  readonly status$ = this.statusSubject.asObservable();

  constructor(
    private database: OfflineDatabaseService,
    private queue: SyncQueueRepository,
  ) {}

  get status(): OfflineSyncStatus {
    return this.statusSubject.value;
  }

  registerModule(handler: OfflineSyncModuleHandler): () => void {
    const module = String(handler.module || '').trim();
    if (!module) throw new Error('O módulo de sincronização precisa possuir uma chave.');
    if (this.handlers.has(module)) throw new Error(`O módulo offline “${module}” já foi registrado.`);

    this.handlers.set(module, handler);
    if (this.activeUserId && this.status.connected) void this.syncNow();

    return () => {
      if (this.handlers.get(module) === handler) this.handlers.delete(module);
    };
  }

  async startForUser(userId: number): Promise<boolean> {
    const normalizedUserId = Math.trunc(Number(userId || 0));
    const token = ++this.lifecycleToken;

    if (!Capacitor.isNativePlatform()) {
      this.publish({ state: 'indisponivel', userId: 0, connected: true, summary: { ...EMPTY_SUMMARY } });
      return false;
    }
    if (normalizedUserId <= 0) {
      await this.stop();
      return false;
    }
    if (this.activeUserId === normalizedUserId && this.database.isReady) {
      return true;
    }

    if (this.activeUserId && this.activeUserId !== normalizedUserId) {
      this.activeUserId = 0;
      await this.syncingPromise?.catch(() => undefined);
      this.syncingPromise = null;
    }

    this.publish({ state: 'preparando', userId: normalizedUserId, connected: this.status.connected });
    const initialized = await this.database.initializeForUser(normalizedUserId);
    if (token !== this.lifecycleToken) return false;
    if (!initialized) {
      this.publish({
        state: 'erro',
        userId: normalizedUserId,
        connected: this.status.connected,
        error: this.database.status.error || 'Não foi possível inicializar o banco offline.',
      });
      return false;
    }

    this.activeUserId = normalizedUserId;
    await this.queue.restoreInterrupted();
    await this.queue.purgeCompleted();
    await this.ensureNetworkListener();
    const network = await Network.getStatus();
    const summary = await this.queue.getSummary();
    this.publish({
      state: network.connected ? 'pronto' : 'offline',
      userId: normalizedUserId,
      connected: network.connected,
      summary,
    });

    if (network.connected) void this.syncNow();
    return true;
  }

  async stop(): Promise<void> {
    ++this.lifecycleToken;
    this.activeUserId = 0;
    await this.syncingPromise?.catch(() => undefined);
    this.syncingPromise = null;
    await this.database.close();
    this.publish({
      state: Capacitor.isNativePlatform() ? 'inativo' : 'indisponivel',
      userId: 0,
      connected: this.status.connected,
      summary: { ...EMPTY_SUMMARY },
    });
  }

  async enqueue(input: OfflineSyncOperationInput): Promise<OfflineSyncOperation> {
    return this.enqueueWithLocalChanges([], input);
  }

  async enqueueWithLocalChanges(
    localTasks: capTask[],
    input: OfflineSyncOperationInput,
  ): Promise<OfflineSyncOperation> {
    if (!this.activeUserId || !this.database.isReady) {
      throw new Error('A sessão offline ainda não está pronta.');
    }
    const operation = await this.queue.enqueueWithLocalChanges(localTasks, input);
    await this.refreshSummary();
    if (this.status.connected) void this.syncNow();
    return operation;
  }

  syncNow(): Promise<void> {
    if (this.syncingPromise) return this.syncingPromise;
    if (!this.activeUserId || !this.database.isReady || !this.status.connected) {
      return Promise.resolve();
    }

    this.syncingPromise = this.performSync().finally(() => {
      this.syncingPromise = null;
    });
    return this.syncingPromise;
  }

  private async performSync(): Promise<void> {
    const userId = this.activeUserId;
    let syncError = '';
    this.publish({ state: 'sincronizando', userId, connected: true });

    try {
      for (const handler of this.handlers.values()) {
        if (!this.status.connected || userId !== this.activeUserId) break;
        await this.pushModule(handler);
        await this.pullModule(handler);
      }
      if (userId === this.activeUserId) {
        this.publish({ lastSyncAt: new Date().toISOString() });
      }
    } catch (error) {
      syncError = this.errorMessage(error);
    } finally {
      if (userId === this.activeUserId && this.database.isReady) {
        await this.refreshSummary();
        this.publish({
          state: syncError ? 'erro' : (this.status.connected ? 'pronto' : 'offline'),
          error: syncError || undefined,
        });
      }
    }
  }

  private async pushModule(handler: OfflineSyncModuleHandler): Promise<void> {
    let processedAny = false;
    for (let cycle = 0; cycle < 20 && this.status.connected && this.activeUserId; cycle++) {
      const operations = await this.queue.listPending(handler.module, 25);
      if (!operations.length) break;

      for (const operation of operations) {
        if (!this.status.connected || !this.activeUserId) break;
        if (!await this.queue.markProcessing(operation.operationUuid)) continue;

        try {
          await handler.push(operation);
          await this.queue.markCompleted(operation.operationUuid);
          processedAny = true;
        } catch (error) {
          if (error instanceof OfflineSyncConflictError) {
            await this.queue.markConflict(
              operation,
              error.message,
              error.serverPayload,
              error.localPayload,
            );
          } else {
            await this.queue.markFailure(operation, error);
          }
        }
      }

      if (operations.length < 25) break;
    }

    if (processedAny) await this.queue.updatePushState(handler.module);
  }

  private async pullModule(handler: OfflineSyncModuleHandler): Promise<void> {
    if (!handler.pull || !this.status.connected) return;

    let cursor = await this.queue.getCursor(handler.module);
    try {
    for (let page = 0; page < 20 && this.status.connected && this.activeUserId; page++) {
        const result = await handler.pull(cursor);
        cursor = result.cursor;
        await this.queue.updatePullState(handler.module, cursor);
        if (!result.hasMore) break;
      }
    } catch (error) {
      await this.queue.registerGeneralError(handler.module, error);
      throw error;
    }
  }

  private async ensureNetworkListener(): Promise<void> {
    if (this.networkListener) return;
    this.networkListener = await Network.addListener('networkStatusChange', (status) => {
      this.publish({
        state: status.connected ? 'pronto' : 'offline',
        connected: status.connected,
        error: undefined,
      });
      if (status.connected && this.activeUserId) void this.syncNow();
    });
  }

  private async refreshSummary(): Promise<void> {
    if (!this.database.isReady) return;
    this.publish({ summary: await this.queue.getSummary() });
  }

  private publish(changes: Partial<OfflineSyncStatus>): void {
    this.statusSubject.next({
      ...this.statusSubject.value,
      ...changes,
      summary: changes.summary || this.statusSubject.value.summary,
    });
  }

  private errorMessage(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    try {
      return JSON.stringify(error);
    } catch {
      return 'Falha desconhecida durante a sincronização offline.';
    }
  }
}
